import { keys } from "lodash";

import { type Sale } from "services/types/sales.type";

import {
  PA_TAG_VALUES,
  withPurchaseAgreementTags
} from "./purchase-agreement-tags.utils";
import { type MergeTagMenuEntry } from "./signature-merge-tags.utils";

export const PATIENT_SELECTED_INVOICE_OPTIONS = {
  SELECT_AN_INVOICE: "Patient.SelectedInvoice.SelectAnInvoice",
  SALE_ITEMS: "Patient.SelectedInvoice.LineItems",
  SERVICES: "Patient.SelectedInvoice.Services",
  SERVICES_NO_PRICE: "Patient.SelectedInvoice.ServicesNoPrice",
  DEVICES: "Patient.SelectedInvoice.Devices",
  DEVICES_NO_PRICE: "Patient.SelectedInvoice.DevicesNoPrice",
  TOTALS: "Patient.SelectedInvoice.Totals",
  TOTAL_COST_OF_HEARING_AIDS: "Patient.SelectedInvoice.TotalCostOfHearingAids",
  TOTAL_COST_OF_SERVICES: "Patient.SelectedInvoice.TotalCostOfServices",
  TOTAL_COST_OF_HEARING_AIDS_AFTER_DISCOUNTS:
    "Patient.SelectedInvoice.TotalCostOfHearingAidsAfterDiscounts",
  TOTAL_COST_OF_SERVICES_AFTER_DISCOUNTS:
    "Patient.SelectedInvoice.TotalCostOfServicesAfterDiscounts",
  TOTAL_AMOUNT: "Patient.SelectedInvoice.TotalAmount",
  BALANCE_DUE: "Patient.SelectedInvoice.BalanceDue",
  // ENG-2463: the card surcharge collected on the sale, for a form's credit card fee line.
  CREDIT_CARD_FEE: "Patient.SelectedInvoice.CreditCardFee",
  // ENG-2463: a converted purchase agreement's items table as the PDF draws it, filled from the sale. The
  // converter writes it with the table's layout as options; it is not in the editor's menu.
  ITEMS_AS_DRAWN: "Patient.SelectedInvoice.ItemsAsDrawn",
  DEFINE_PARAMETERS: "Patient.SelectedInvoice.DefineParameters",
  WITH_PREDEFINED_PARAMETERS:
    "Patient.SelectedInvoice.WithPredefinedParameters",
  TRIAL_PERIOD_DURATION_DAYS: "Patient.SelectedInvoice.TrialPeriodDurationDays",
  RESTOCKING_RETURN_FEE: "Patient.SelectedInvoice.RestockingReturnFee",
  LOSS_DAMAGE_DEDUCTIBLE: "Patient.SelectedInvoice.LossDamageDeductible",
  WARRANTY_DURATION: "Patient.SelectedInvoice.WarrantyDuration",
  MEMO: "Patient.SelectedInvoice.Memo",
  PURCHASE_AGREEMENT_DATE: "Patient.SelectedInvoice.PurchaseAgreementDate",
  TRIAL_PERIOD_END_DATE: "Patient.SelectedInvoice.TrialPeriodEndDate",
  PROVIDER_FULL_NAME: "Patient.SelectedInvoice.ProviderFullName",
  PROVIDER_FULL_NAME_WITH_TITLE:
    "Patient.SelectedInvoice.ProviderFullNameWithTitle",
  PROVIDER_FIRST_NAME: "Patient.SelectedInvoice.ProviderFirstName",
  PROVIDER_LAST_NAME: "Patient.SelectedInvoice.ProviderLastName",
  PROVIDER_TITLE: "Patient.SelectedInvoice.ProviderTitle",
  PROVIDER_SUFFIX: "Patient.SelectedInvoice.ProviderSuffix",
  PROVIDER_LICENSE_NUMBER: "Patient.SelectedInvoice.ProviderLicenseNumber",
  PROVIDER_SIGNATURE: "Patient.SelectedInvoice.ProviderSignature",

  // Left Hearing Aid
  LEFT_HA_MANUFACTURER: "Patient.SelectedInvoice.LeftHearingAid.Manufacturer",
  LEFT_HA_MODEL: "Patient.SelectedInvoice.LeftHearingAid.Model",
  // ENG-2477: the Catalog name (Inventory › Catalog), beside the shared catalog's model.
  LEFT_HA_NAME: "Patient.SelectedInvoice.LeftHearingAid.Name",
  LEFT_HA_SERIAL_NUMBER: "Patient.SelectedInvoice.LeftHearingAid.SerialNumber",
  LEFT_HA_WARRANTY_START:
    "Patient.SelectedInvoice.LeftHearingAid.WarrantyStart",
  LEFT_HA_WARRANTY_END: "Patient.SelectedInvoice.LeftHearingAid.WarrantyEnd",

  // Right Hearing Aid
  RIGHT_HA_MANUFACTURER: "Patient.SelectedInvoice.RightHearingAid.Manufacturer",
  RIGHT_HA_MODEL: "Patient.SelectedInvoice.RightHearingAid.Model",
  RIGHT_HA_NAME: "Patient.SelectedInvoice.RightHearingAid.Name",
  RIGHT_HA_SERIAL_NUMBER:
    "Patient.SelectedInvoice.RightHearingAid.SerialNumber",
  RIGHT_HA_WARRANTY_START:
    "Patient.SelectedInvoice.RightHearingAid.WarrantyStart",
  RIGHT_HA_WARRANTY_END: "Patient.SelectedInvoice.RightHearingAid.WarrantyEnd",

  // Hearing Aid Delivery Date
  LEFT_HA_DELIVERY_DATE: "Patient.SelectedInvoice.LeftHearingAid.DeliveryDate",
  RIGHT_HA_DELIVERY_DATE:
    "Patient.SelectedInvoice.RightHearingAid.DeliveryDate",
  HA_DELIVERY_DATE: "Patient.SelectedInvoice.HearingAids.DeliveryDate",

  // Both Hearing Aids (binaural) expiration dates. Resolve like HA_DELIVERY_DATE:
  // one value when both ears match, "L: … R: …" when they differ, one-sided prefix
  // when only one ear has a value.
  HA_MANUFACTURER_WARRANTY_EXPIRATION_DATE:
    "Patient.SelectedInvoice.HearingAids.ManufacturerWarrantyExpirationDate",
  HA_LOSS_DAMAGE_WARRANTY_EXPIRATION_DATE:
    "Patient.SelectedInvoice.HearingAids.LossDamageWarrantyExpirationDate",
  HA_SERVICE_PLAN_EXPIRATION_DATE:
    "Patient.SelectedInvoice.HearingAids.ServicePlanExpirationDate",

  // Left Receiver
  LEFT_RECEIVER_MANUFACTURER:
    "Patient.SelectedInvoice.LeftReceiver.Manufacturer",
  LEFT_RECEIVER_MODEL: "Patient.SelectedInvoice.LeftReceiver.Model",
  LEFT_RECEIVER_GAIN: "Patient.SelectedInvoice.LeftReceiver.Gain",
  LEFT_RECEIVER_LENGTH: "Patient.SelectedInvoice.LeftReceiver.Length",

  // Right Receiver
  RIGHT_RECEIVER_MANUFACTURER:
    "Patient.SelectedInvoice.RightReceiver.Manufacturer",
  RIGHT_RECEIVER_MODEL: "Patient.SelectedInvoice.RightReceiver.Model",
  RIGHT_RECEIVER_GAIN: "Patient.SelectedInvoice.RightReceiver.Gain",
  RIGHT_RECEIVER_LENGTH: "Patient.SelectedInvoice.RightReceiver.Length",

  // Left Dome
  LEFT_DOME_MANUFACTURER: "Patient.SelectedInvoice.LeftDome.Manufacturer",
  LEFT_DOME_MODEL: "Patient.SelectedInvoice.LeftDome.Model",
  LEFT_DOME_SIZE: "Patient.SelectedInvoice.LeftDome.Size",
  LEFT_DOME_TYPE: "Patient.SelectedInvoice.LeftDome.Type",

  // Right Dome
  RIGHT_DOME_MANUFACTURER: "Patient.SelectedInvoice.RightDome.Manufacturer",
  RIGHT_DOME_MODEL: "Patient.SelectedInvoice.RightDome.Model",
  RIGHT_DOME_SIZE: "Patient.SelectedInvoice.RightDome.Size",
  RIGHT_DOME_TYPE: "Patient.SelectedInvoice.RightDome.Type"
};

export const PATIENT_SELECTED_INVOICE_MENU_OPTIONS = {
  title: "Patient - Invoice",
  menu: [
    {
      title: "Select Invoice…",
      value: PATIENT_SELECTED_INVOICE_OPTIONS.SELECT_AN_INVOICE
    },
    {
      title: "With Predefined Parameters",
      menu: [
        {
          title: "Define Invoice Parameters…",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.DEFINE_PARAMETERS
        },
        {
          title: "Patient - Invoice - With Predefined Parameters",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.WITH_PREDEFINED_PARAMETERS
        }
      ]
    },
    {
      title: "Patient - Invoice - Line Items",
      value: PATIENT_SELECTED_INVOICE_OPTIONS.SALE_ITEMS
    },
    {
      title: "Patient - Invoice - Devices",
      value: PATIENT_SELECTED_INVOICE_OPTIONS.DEVICES
    },
    {
      title: "Patient - Invoice - Devices (No Price)",
      value: PATIENT_SELECTED_INVOICE_OPTIONS.DEVICES_NO_PRICE
    },
    {
      title: "Patient - Invoice - Services",
      value: PATIENT_SELECTED_INVOICE_OPTIONS.SERVICES
    },
    {
      title: "Patient - Invoice - Services (No Price)",
      value: PATIENT_SELECTED_INVOICE_OPTIONS.SERVICES_NO_PRICE
    },

    {
      title: "Patient - Invoice - Totals",
      value: PATIENT_SELECTED_INVOICE_OPTIONS.TOTALS
    },
    {
      title: "Patient - Invoice - Total Cost of Hearing Aids",
      value: PATIENT_SELECTED_INVOICE_OPTIONS.TOTAL_COST_OF_HEARING_AIDS
    },
    {
      title: "Patient - Invoice - Total Cost of Services",
      value: PATIENT_SELECTED_INVOICE_OPTIONS.TOTAL_COST_OF_SERVICES
    },
    {
      title: "Patient - Invoice - Total Cost of Hearing Aids After Discounts",
      value:
        PATIENT_SELECTED_INVOICE_OPTIONS.TOTAL_COST_OF_HEARING_AIDS_AFTER_DISCOUNTS
    },
    {
      title: "Patient - Invoice - Total Cost of Services After Discounts",
      value:
        PATIENT_SELECTED_INVOICE_OPTIONS.TOTAL_COST_OF_SERVICES_AFTER_DISCOUNTS
    },
    {
      title: "Patient - Invoice - Total Amount",
      value: PATIENT_SELECTED_INVOICE_OPTIONS.TOTAL_AMOUNT
    },
    {
      title: "Patient - Invoice - Balance Due",
      value: PATIENT_SELECTED_INVOICE_OPTIONS.BALANCE_DUE
    },
    {
      title: "Patient - Invoice - Credit Card Fee",
      value: PATIENT_SELECTED_INVOICE_OPTIONS.CREDIT_CARD_FEE
    },
    {
      title: "Miscellaneous",
      menu: [
        {
          title: "Patient - Invoice - Purchase Agreement Date",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.PURCHASE_AGREEMENT_DATE
        },
        {
          title: "Patient - Invoice - Trial Period Duration Days",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.TRIAL_PERIOD_DURATION_DAYS
        },
        {
          title: "Patient - Invoice - Trial Period End Date",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.TRIAL_PERIOD_END_DATE
        },

        {
          title: "Patient - Invoice - Restocking Return Fee",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.RESTOCKING_RETURN_FEE
        },

        {
          title: "Patient - Invoice - Loss Damage Deductible",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.LOSS_DAMAGE_DEDUCTIBLE
        },

        {
          title: "Patient - Invoice - Warranty Duration",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.WARRANTY_DURATION
        },
        {
          title: "Patient - Invoice - Memo",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.MEMO
        }
      ]
    },
    {
      title: "Provider",
      menu: [
        {
          title: "Patient - Invoice - Provider - Full Name",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_FULL_NAME
        },
        {
          title: "Patient - Invoice - Provider - Full Name With Title",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_FULL_NAME_WITH_TITLE
        },
        {
          title: "Patient - Invoice - Provider - First Name",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_FIRST_NAME
        },
        {
          title: "Patient - Invoice - Provider - Last Name",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_LAST_NAME
        },
        {
          title: "Patient - Invoice - Provider - Title",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_TITLE
        },
        {
          title: "Patient - Invoice - Provider - Suffix",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_SUFFIX
        },
        {
          title: "Patient - Invoice - Provider - License Number",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_LICENSE_NUMBER
        },
        {
          title: "Patient - Invoice - Provider - Signature",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.PROVIDER_SIGNATURE
        }
      ]
    },
    {
      title: "Left Hearing Aid",
      menu: [
        {
          title: "Patient - Invoice - Left Hearing Aid - Manufacturer",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_MANUFACTURER
        },
        {
          title: "Patient - Invoice - Left Hearing Aid - Model",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_MODEL
        },
        {
          title:
            "Patient - Invoice - Left Hearing Aid - Name (as in your Catalog)",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_NAME
        },
        {
          title: "Patient - Invoice - Left Hearing Aid - Serial Number",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_SERIAL_NUMBER
        },
        {
          title:
            "Patient - Invoice - Left Hearing Aid - Manufacturer Warranty Start Date",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_WARRANTY_START
        },
        {
          title:
            "Patient - Invoice - Left Hearing Aid - Manufacturer Warranty End Date",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_WARRANTY_END
        },
        {
          title: "Patient - Invoice - Left Hearing Aid - Delivery Date",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_HA_DELIVERY_DATE
        }
      ]
    },
    {
      title: "Right Hearing Aid",
      menu: [
        {
          title: "Patient - Invoice - Right Hearing Aid - Manufacturer",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_MANUFACTURER
        },
        {
          title: "Patient - Invoice - Right Hearing Aid - Model",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_MODEL
        },
        {
          title:
            "Patient - Invoice - Right Hearing Aid - Name (as in your Catalog)",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_NAME
        },
        {
          title: "Patient - Invoice - Right Hearing Aid - Serial Number",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_SERIAL_NUMBER
        },
        {
          title:
            "Patient - Invoice - Right Hearing Aid - Manufacturer Warranty Start Date",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_WARRANTY_START
        },
        {
          title:
            "Patient - Invoice - Right Hearing Aid - Manufacturer Warranty End Date",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_WARRANTY_END
        },
        {
          title: "Patient - Invoice - Right Hearing Aid - Delivery Date",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_HA_DELIVERY_DATE
        }
      ]
    },
    {
      title: "Patient - Invoice - Both Hearing Aids - Delivery Date",
      value: PATIENT_SELECTED_INVOICE_OPTIONS.HA_DELIVERY_DATE
    },
    {
      title:
        "Patient - Invoice - Both Hearing Aids - Manufacturer Warranty Expiration Date",
      value:
        PATIENT_SELECTED_INVOICE_OPTIONS.HA_MANUFACTURER_WARRANTY_EXPIRATION_DATE
    },
    {
      title:
        "Patient - Invoice - Both Hearing Aids - Loss & Damage Warranty Expiration Date",
      value:
        PATIENT_SELECTED_INVOICE_OPTIONS.HA_LOSS_DAMAGE_WARRANTY_EXPIRATION_DATE
    },
    {
      title:
        "Patient - Invoice - Both Hearing Aids - Service Plan Expiration Date",
      value: PATIENT_SELECTED_INVOICE_OPTIONS.HA_SERVICE_PLAN_EXPIRATION_DATE
    },
    {
      title: "Left Receiver",
      menu: [
        {
          title: "Patient - Invoice - Left Receiver - Manufacturer",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_RECEIVER_MANUFACTURER
        },
        {
          title: "Patient - Invoice - Left Receiver - Model",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_RECEIVER_MODEL
        },
        {
          title: "Patient - Invoice - Left Receiver - Gain",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_RECEIVER_GAIN
        },
        {
          title: "Patient - Invoice - Left Receiver - Length",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_RECEIVER_LENGTH
        }
      ]
    },
    {
      title: "Right Receiver",
      menu: [
        {
          title: "Patient - Invoice - Right Receiver - Manufacturer",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_RECEIVER_MANUFACTURER
        },
        {
          title: "Patient - Invoice - Right Receiver - Model",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_RECEIVER_MODEL
        },
        {
          title: "Patient - Invoice - Right Receiver - Gain",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_RECEIVER_GAIN
        },
        {
          title: "Patient - Invoice - Right Receiver - Length",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_RECEIVER_LENGTH
        }
      ]
    },
    {
      title: "Left Dome",
      menu: [
        {
          title: "Patient - Invoice - Left Dome - Manufacturer",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_DOME_MANUFACTURER
        },
        {
          title: "Patient - Invoice - Left Dome - Model",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_DOME_MODEL
        },
        {
          title: "Patient - Invoice - Left Dome - Size",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_DOME_SIZE
        },
        {
          title: "Patient - Invoice - Left Dome - Type",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.LEFT_DOME_TYPE
        }
      ]
    },
    {
      title: "Right Dome",
      menu: [
        {
          title: "Patient - Invoice - Right Dome - Manufacturer",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_DOME_MANUFACTURER
        },
        {
          title: "Patient - Invoice - Right Dome - Model",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_DOME_MODEL
        },
        {
          title: "Patient - Invoice - Right Dome - Size",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_DOME_SIZE
        },
        {
          title: "Patient - Invoice - Right Dome - Type",
          value: PATIENT_SELECTED_INVOICE_OPTIONS.RIGHT_DOME_TYPE
        }
      ]
    }
  ]
};

// ENG-2492: with `digital-form-tags-purchase-agreement` on, the menu carries every purchase agreement
// value as its own tag. Off, it is this constant unchanged.
export const getInvoiceMenu = (purchaseAgreementTags?: boolean) =>
  purchaseAgreementTags
    ? {
        ...PATIENT_SELECTED_INVOICE_MENU_OPTIONS,
        menu: withPurchaseAgreementTags(
          PATIENT_SELECTED_INVOICE_MENU_OPTIONS.menu
        )
      }
    : PATIENT_SELECTED_INVOICE_MENU_OPTIONS;

export const getPatientSelectedInvoiceMenuOptions = ({
  allowSelectInvoice,
  allowInvoiceMergeTagsFeature,
  sale,
  purchaseAgreementTags
}: {
  allowSelectInvoice?: boolean;
  allowInvoiceMergeTagsFeature?: boolean;
  sale?: Sale;
  purchaseAgreementTags?: boolean;
}): MergeTagMenuEntry | Array<never> => {
  if (!allowInvoiceMergeTagsFeature) return [];
  const invoiceMenu = getInvoiceMenu(purchaseAgreementTags);
  if (!allowSelectInvoice) {
    return {
      title: invoiceMenu.title,
      menu: invoiceMenu.menu.filter(
        (item) =>
          item.value !== PATIENT_SELECTED_INVOICE_OPTIONS.SELECT_AN_INVOICE
      )
    };
  } else if (!sale) {
    return {
      title: PATIENT_SELECTED_INVOICE_MENU_OPTIONS.title,
      menu: PATIENT_SELECTED_INVOICE_MENU_OPTIONS.menu.filter(
        (item) =>
          item.value === PATIENT_SELECTED_INVOICE_OPTIONS.SELECT_AN_INVOICE
      )
    };
  }
  return invoiceMenu;
};

export const shouldSelectInvoiceByTextContent = (text: string) => {
  return (
    keys(PATIENT_SELECTED_INVOICE_OPTIONS).some((key) =>
      text.includes(
        `{{${PATIENT_SELECTED_INVOICE_OPTIONS[key as keyof typeof PATIENT_SELECTED_INVOICE_OPTIONS]}}}`
      )
    ) ||
    // ENG-2492: a template holding only purchase agreement tags needs an invoice too, flag on or off
    // (a template saved while the flag was on keeps its tags after it is turned off).
    [...PA_TAG_VALUES].some((tag) => text.includes(`{{${tag}}}`))
  );
};
